import { BarcodeOutlined, CheckSquareOutlined, InboxOutlined, OrderedListOutlined, ScanOutlined, SearchOutlined, SendOutlined, SwapOutlined } from "@ant-design/icons";
import type { ScanResult } from "@wms/domain";
import { Alert, App, Card, Input, Space, Typography } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, describeError } from "../api/client";
import { useAuthStore } from "../auth/store";
import { CameraScanButton } from "../components/CameraScanner";
import { PageHeader } from "../components/PageHeader";
import { ScanResultModal } from "../components/ScanResultModal";

interface Action {
  to: string;
  title: string;
  hint: string;
  icon: React.ReactNode;
  permission: string;
}

const ACTIONS: Action[] = [
  { to: "/expected_receipts", title: "Принять товар", hint: "Поступления от поставщиков: открыть документ и нажать «Принять товар»", icon: <InboxOutlined />, permission: "inbound.expected_receipts.read" },
  { to: "/tasks?mine=1", title: "Мои задания", hint: "Разложить по местам, пополнить, переместить — что назначено вам", icon: <OrderedListOutlined />, permission: "tasks.tasks.read" },
  { to: "/shipment_orders", title: "Собрать заказ", hint: "Заказы на отгрузку: «Собрать заказ», затем «Отгрузить заказ»", icon: <SendOutlined />, permission: "outbound.shipment_orders.read" },
  { to: "/stock_counts", title: "Пересчитать товар", hint: "Инвентаризация ячейки, зоны или товара", icon: <CheckSquareOutlined />, permission: "inventory.stock_counts.read" },
  { to: "/stock-summary", title: "Где лежит товар", hint: "Остатки по товарам и ячейкам: на складе, отложено, можно взять", icon: <SearchOutlined />, permission: "inventory.stock.read" },
  { to: "/locations", title: "Переместить", hint: "Карточка ячейки: «Переместить всё» или перенос паллеты", icon: <SwapOutlined />, permission: "topology.locations.read" },
];

/**
 * «Работа на складе» (UX audit 5.1): a start screen for shift staff with a few big verb buttons and one code field.
 * Everything administrative stays in the menu of the manager and the administrator.
 */
export function WorkPage() {
  const can = useAuthStore((s) => s.can);
  const me = useAuthStore((s) => s.me);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [code, setCode] = useState("");
  const [lookup, setLookup] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);

  const go = async (value: string) => {
    const text = value.trim();
    if (!text) return;
    try {
      // one recognition for every kind of code: product, piece, box, pallet, cargo unit, cell, document (section 4)
      const scanned = await api.get<ScanResult>("/scan", { value: text });
      if (scanned.path && !["ambiguous", "unknown", "invalid", "forbidden"].includes(scanned.kind)) {
        message.success(scanned.label ?? scanned.value, 3);
        navigate(scanned.path);
        return;
      }
      setLookup(null);
      setScanResult(scanned);
    } catch (e) {
      message.error(describeError(e));
    }
  };

  const visible = ACTIONS.filter((a) => can(a.permission));
  return (
    <div>
      <PageHeader title={`Работа на складе${me ? `, ${me.user.full_name.split(" ")[0]}` : ""}`} subtitle="Выберите, что нужно сделать, или отсканируйте код" />
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }} size={6}>
          <Typography.Text strong style={{ fontSize: 16 }}>
            <ScanOutlined /> Сканировать или ввести код
          </Typography.Text>
          <Input.Search
            size="large"
            autoFocus
            allowClear
            enterButton="Найти"
            placeholder="Штрихкод товара, адрес ячейки или номер документа"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onSearch={go}
            prefix={<BarcodeOutlined />}
          />
          <Space wrap>
            <CameraScanButton onScan={(value) => { setCode(value); void go(value); }} />
            <Typography.Text type="secondary">Сканер терминала работает как клавиатура: наведите на штрихкод и нажмите на курок. На телефоне — «Сканировать камерой».</Typography.Text>
          </Space>
          {lookup && <Alert type="warning" showIcon message={`Код «${lookup}» не найден. Проверьте, что товар заведён и у него есть штрихкод, или введите часть адреса ячейки.`} />}
          {scanResult && <ScanResultModal result={scanResult} onClose={() => setScanResult(null)} />}
        </Space>
      </Card>
      <div className="wms-work-grid">
        {visible.map((a) => (
          <Link key={a.to} to={a.to} className="wms-work-card">
            {a.icon}
            <span>{a.title}</span>
            <small>{a.hint}</small>
          </Link>
        ))}
      </div>
      {visible.length === 0 && <Alert type="info" showIcon message="Вам пока не выданы права на складские операции. Обратитесь к руководителю." />}
    </div>
  );
}
